/**
 * Die Karte „Hörprobe: Sofort-Sätze“ — Martins blinde Hörprobe der Reflex-Sätze. (17.10.2026, Jarvis, Ticket 2738)
 *
 * Bevor Vespera auf „Danke“, „Hallo“, „Tschüss“ oder „Hörst du mich?“ je sofort mit einem festen Satz antworten darf
 * (REFLEX_ANTWORT=an, docs/jarvis-an-entwurf.md), entscheidet Martins Ohr, ob die Sätze nach Vespera klingen.
 *
 *   - Die Sätze kommen aus dem EINEN Vorrat (shared/reflex-vorrat.ts) — dieselben, die der Server sprechen würde.
 *   - Blind: gemischt, ohne Art und ohne Text vorab. Gezeigt wird nur „Satz 4 von 18“; Text und Art stehen erst in der
 *     Auswertung, nachdem der Satz bewertet ist.
 *   - Gemischt, aber STABIL je Sitzung: die Saat liegt im sessionStorage. Ein Neuladen mitten in der Probe ändert die
 *     Reihenfolge nicht; bewertete Sätze werden übersprungen (es geht weiter, wo Martin aufgehört hat).
 *   - Gesprochen über EXAKT Vesperas Antworten-Weg (lib/vespera-satz.ts → app.ts `fetchTtsChunk`): Stimme und Tempo der
 *     Konto-Einstellung, Datei-/PCM-Weg nach denselben Schaltern, dieselbe Vorlese-Schlange mit ihren Signalen an
 *     Tagesaufnahme und Mikro, Eintritt in den Ton-Leitstand. Bis 17.10. (erste Fassung) ging es über /api/tts/stream —
 *     den für Antworten abgeschalteten Strom-Weg ohne Tempo (Prüfbefund).
 *   - Gespeichert wird je Satz-Kennung nur „passt“ ja/nein (server/reflex-hoerprobe.ts), nur für Konto 1.
 *   - Durchgefallene Sätze werden NICHT automatisch entfernt; die Karte zeigt sie, Martin entscheidet.
 *
 * Keine Inline-Styles: Aussehen in frontend/style.css („REFLEX-HÖRPROBE“).
 */
import { REFLEX_ART_NAME, reflexSaetze, type ReflexSatz } from "../../shared/reflex-vorrat.js";
import { sprichVesperaSatz } from "./vespera-satz.js";

export const HOERPROBE_PFAD = "/api/reflex-hoerprobe";
const SAAT_SCHLUESSEL = "reflexHoerprobeSaat";
/** Länger darf ein einzelner Satz (Vertonung + Sprechen) nicht dauern. */
const SPIEL_FRIST_MS = 20_000;
/** So lange wartet „Von vorn“ auf die Bestätigung durch einen zweiten Tipp. */
const BESTAETIGEN_MS = 5_000;

/** Eine Saat (32 Bit) — fest für diese Sitzung des Browsers. Ohne Speicher (privat, gesperrt): je Karte eine neue. */
export function sitzungsSaat(speicher: Pick<Storage, "getItem" | "setItem"> | null, zufall: () => number = Math.random): number {
  try {
    const alt = Number(speicher?.getItem(SAAT_SCHLUESSEL));
    if (Number.isInteger(alt) && alt > 0) return alt;
  } catch {
    /* gesperrt */
  }
  const neu = 1 + Math.floor(zufall() * 0xfffffffe);
  try {
    speicher?.setItem(SAAT_SCHLUESSEL, String(neu));
  } catch {
    /* gesperrt: dann nur für diese Karte */
  }
  return neu;
}

/** Eine neue Saat erzwingen („Von vorn“). */
export function neueSaat(speicher: Pick<Storage, "setItem"> | null, zufall: () => number = Math.random): number {
  const neu = 1 + Math.floor(zufall() * 0xfffffffe);
  try {
    speicher?.setItem(SAAT_SCHLUESSEL, String(neu));
  } catch {
    /* gesperrt */
  }
  return neu;
}

/** Mischen mit Saat (Mulberry32 + Fisher-Yates): gleiche Saat, gleiche Reihenfolge. Rein. */
export function mische<T>(liste: readonly T[], saat: number): T[] {
  let a = saat >>> 0;
  const zufall = (): number => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const r = [...liste];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(zufall() * (i + 1));
    const t = r[i];
    r[i] = r[j];
    r[j] = t;
  }
  return r;
}

export interface Bewertung {
  kennung: string;
  passt: boolean;
}

/** Die durchgefallenen Sätze (in Vorrats-Reihenfolge) — nur gültige Kennungen. Rein. */
export function durchgefallen(bewertungen: readonly Bewertung[], saetze: readonly ReflexSatz[] = reflexSaetze()): ReflexSatz[] {
  const nein = new Set(bewertungen.filter((b) => !b.passt).map((b) => b.kennung));
  return saetze.filter((s) => nein.has(s.kennung));
}

export interface HoerprobeUmgebung {
  holen: typeof fetch;
  /** Spricht den Satz mit Vesperas Stimme und kehrt nach dem Ende zurück. Wirft bei Fehler. */
  spiele: (text: string, signal: AbortSignal) => Promise<void>;
  /** sessionStorage oder null. */
  speicher: Pick<Storage, "getItem" | "setItem"> | null;
}

function standardSpeicher(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function standardUmgebung(): HoerprobeUmgebung {
  return {
    holen: (...a) => fetch(...a),
    // Vesperas Antworten-Weg (lib/vespera-satz.ts): dieselbe Vertonung, Schlange, Leitstand und Signale an Aufnahme/Mikro.
    spiele: async (text, signal) => {
      // Ein hängender Ton darf die Karte nicht festhalten: nach SPIEL_FRIST_MS abbrechen und als Fehler melden.
      // ⚠️ Pausiert Martin die Probe über den Sperrbildschirm, läuft die Frist weiter — nach 20 s kommt die Fehlerzeile
      //    („bitte noch einmal“) statt eines Fortsetzens. Hingenommen: ein Probesatz dauert 1–2 s.
      const frist = new AbortController();
      const weiter = (): void => frist.abort();
      signal.addEventListener("abort", weiter, { once: true });
      const uhr = setTimeout(() => frist.abort(), SPIEL_FRIST_MS);
      try {
        await sprichVesperaSatz(text, frist.signal);
        if (frist.signal.aborted && !signal.aborted) throw new Error("frist");
      } finally {
        clearTimeout(uhr);
        signal.removeEventListener("abort", weiter);
      }
    },
    speicher: standardSpeicher(),
  };
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

function knopf(klasse: string, text: string): HTMLButtonElement {
  const b = el("button", `settings-btn ${klasse}`, text);
  b.type = "button";
  return b;
}

/** Die Karte. EINE Einbaustelle: Einstellungen › Erweitert, nur Konto 1 (der Server weist andere ab). */
export function reflexHoerprobeKarte(umgebung: HoerprobeUmgebung = standardUmgebung()): HTMLElement {
  const saetze = reflexSaetze();
  let saat = sitzungsSaat(umgebung.speicher);
  let reihe = mische(saetze, saat);
  const bewertet = new Map<string, boolean>();
  let aktuell: ReflexSatz | null = null;
  let gehoert = false;
  let lauf: AbortController | null = null;
  /** Läuft gerade Laden, Speichern oder Zurücksetzen? Dann sind Urteile und „Von vorn“ zu. */
  let beschaeftigt = true;
  /** Zählt die Runden: eine Antwort aus einer früheren Runde (vor „Von vorn“) wird verworfen. */
  let runde = 0;
  let bestaetigenBis = 0;

  const karte = el("section", "settings-subsection");
  karte.classList.add("reflex-hoerprobe");
  karte.append(el("h3", "settings-subsection-title", "Hörprobe: Sofort-Sätze"));
  karte.append(
    el(
      "p",
      "settings-modules-hint",
      `${saetze.length} kurze Sätze, die Vespera später sofort sagen könnte, ohne nachzudenken. Du hörst sie gemischt und ohne zu wissen, wofür sie gedacht sind. Klingt der Satz nach Vespera?`,
    ),
  );

  const buehne = el("div", "reflex-hoerprobe__buehne");
  const zaehler = el("p", "reflex-hoerprobe__zaehler");
  zaehler.setAttribute("aria-live", "polite");
  const fortschritt = el("meter", "reflex-hoerprobe__fortschritt");
  fortschritt.setAttribute("aria-label", "Fortschritt der Hörprobe");
  fortschritt.setAttribute("min", "0");
  fortschritt.setAttribute("max", String(saetze.length));
  const hoeren = knopf("reflex-hoerprobe__hoeren", "Anhören");
  const urteile = el("div", "reflex-hoerprobe__urteile");
  const passt = knopf("reflex-hoerprobe__passt", "Passt");
  const passtNicht = knopf("settings-btn--ghost", "Passt nicht");
  passtNicht.classList.add("reflex-hoerprobe__passt-nicht");
  urteile.append(passt, passtNicht);
  const regel = el("p", "reflex-hoerprobe__regel", "Erst anhören, dann bewerten — das Urteil gilt dem Klang.");
  regel.id = `reflex-hoerprobe-regel-${Math.random().toString(36).slice(2, 8)}`;
  passt.setAttribute("aria-describedby", regel.id);
  passtNicht.setAttribute("aria-describedby", regel.id);
  buehne.append(zaehler, fortschritt, hoeren, urteile, regel);

  const ergebnis = el("div", "reflex-hoerprobe__ergebnis");
  ergebnis.hidden = true;
  const status = el("p", "reflex-hoerprobe__status");
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  const vonVorn = knopf("settings-btn--ghost", "Von vorn");
  vonVorn.classList.add("reflex-hoerprobe__von-vorn");
  karte.append(buehne, ergebnis, status, vonVorn);

  const sperreUrteile = (zu: boolean): void => {
    passt.disabled = zu;
    passtNicht.disabled = zu;
  };

  const zeigeErgebnis = (): void => {
    const liste = [...bewertet].map(([kennung, p]) => ({ kennung, passt: p }));
    const fallen = durchgefallen(liste, saetze);
    const gut = liste.filter((b) => b.passt).length;
    ergebnis.replaceChildren(
      el("p", "reflex-hoerprobe__summe", `Fertig: ${gut} von ${saetze.length} passen, ${fallen.length} passen nicht.`),
    );
    if (fallen.length) {
      const ul = el("ul", "reflex-hoerprobe__liste");
      for (const s of fallen) {
        const li = el("li", "reflex-hoerprobe__satz");
        li.append(el("span", "reflex-hoerprobe__wortlaut", `„${s.satz}“`), el("span", "reflex-hoerprobe__art", REFLEX_ART_NAME[s.art]));
        ul.append(li);
      }
      ergebnis.append(
        ul,
        el("p", "settings-modules-hint", "Diese Sätze bleiben vorerst im Vorrat. Sag Bescheid, welche raus sollen — dann werden sie ersetzt."),
      );
    } else {
      ergebnis.append(el("p", "settings-modules-hint", "Alle Sätze passen."));
    }
    buehne.hidden = true;
    ergebnis.hidden = false;
  };

  const zeigeNaechsten = (): void => {
    aktuell = reihe.find((s) => !bewertet.has(s.kennung)) ?? null;
    fortschritt.setAttribute("value", String(bewertet.size));
    if (!aktuell) {
      zeigeErgebnis();
      return;
    }
    buehne.hidden = false;
    ergebnis.hidden = true;
    gehoert = false;
    zaehler.textContent = `Satz ${bewertet.size + 1} von ${saetze.length}`;
    hoeren.textContent = "Anhören";
    hoeren.disabled = false;
    sperreUrteile(true);
  };

  hoeren.addEventListener("click", () => {
    const satz = aktuell;
    if (!satz) return;
    lauf?.abort();
    const ac = new AbortController();
    lauf = ac;
    hoeren.disabled = true;
    status.textContent = "Spricht …";
    void (async () => {
      try {
        await umgebung.spiele(satz.satz, ac.signal);
        if (ac.signal.aborted || aktuell !== satz) return;
        gehoert = true;
        status.textContent = "";
      } catch (e) {
        if (!ac.signal.aborted) {
          const grund = e instanceof Error ? e.message : "";
          status.textContent =
            grund === "lange-spur"
              ? "Vespera spielt gerade etwas ab — die Hörprobe wartet, bis es zu Ende ist."
              : grund === "belegt"
              ? "Vespera spricht gerade — bitte warten, dann noch einmal."
              : grund === "unterbrochen"
                ? "Der Satz wurde unterbrochen — bitte noch einmal ganz anhören."
                : "Der Satz ließ sich gerade nicht abspielen — bitte noch einmal.";
        }
      } finally {
        // Abgebrochen (Urteil, „Von vorn“) oder es wird gerade gespeichert: nichts wieder freigeben.
        if (aktuell === satz && lauf === ac && !ac.signal.aborted && !beschaeftigt) {
          hoeren.disabled = false;
          hoeren.textContent = gehoert ? "Noch einmal" : "Anhören";
          sperreUrteile(!gehoert);
        }
      }
    })();
  });

  const urteil = (p: boolean): void => {
    const satz = aktuell;
    if (!satz || !gehoert || beschaeftigt) return;
    beschaeftigt = true;
    const meineRunde = runde;
    sperreUrteile(true);
    hoeren.disabled = true;
    vonVorn.disabled = true;
    lauf?.abort();
    const frei = (): void => {
      beschaeftigt = false;
      vonVorn.disabled = false;
    };
    void (async () => {
      try {
        const r = await umgebung.holen(`${HOERPROBE_PFAD}/bewertung`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ kennung: satz.kennung, passt: p, pruefzahl: satz.pruefzahl }),
        });
        if (meineRunde !== runde) return; // inzwischen „Von vorn“: diese Antwort gehört zur alten Runde
        frei();
        if (!r.ok) {
          status.textContent =
            r.status === 409
              ? "Dieser Satz wurde inzwischen geändert — bitte die Seite neu laden."
              : r.status === 401 || r.status === 403
                ? "Nur für Konto 1."
                : "Die Bewertung ließ sich nicht speichern — bitte noch einmal.";
          sperreUrteile(false);
          hoeren.disabled = false;
          return;
        }
        bewertet.set(satz.kennung, p);
        status.textContent = "";
        zeigeNaechsten();
      } catch {
        if (meineRunde !== runde) return;
        frei();
        status.textContent = "Die Bewertung ließ sich nicht speichern — bitte noch einmal.";
        sperreUrteile(false);
        hoeren.disabled = false;
      }
    })();
  };
  passt.addEventListener("click", () => urteil(true));
  passtNicht.addEventListener("click", () => urteil(false));

  vonVorn.addEventListener("click", () => {
    if (beschaeftigt) return;
    // Zwei Tipps: der erste fragt, der zweite (binnen BESTAETIGEN_MS) löscht.
    if (Date.now() > bestaetigenBis) {
      bestaetigenBis = Date.now() + BESTAETIGEN_MS;
      vonVorn.textContent = "Wirklich alle Bewertungen löschen? Nochmal tippen";
      setTimeout(() => {
        if (Date.now() >= bestaetigenBis) vonVorn.textContent = "Von vorn";
      }, BESTAETIGEN_MS + 50);
      return;
    }
    bestaetigenBis = 0;
    vonVorn.textContent = "Von vorn";
    beschaeftigt = true;
    runde++;
    vonVorn.disabled = true;
    sperreUrteile(true);
    hoeren.disabled = true;
    lauf?.abort();
    void (async () => {
      try {
        const r = await umgebung.holen(HOERPROBE_PFAD, { method: "DELETE", credentials: "same-origin" });
        if (!r.ok) {
          status.textContent = "Ließ sich nicht zurücksetzen.";
          beschaeftigt = false;
          zeigeNaechsten();
          return;
        }
        beschaeftigt = false;
        bewertet.clear();
        saat = neueSaat(umgebung.speicher);
        reihe = mische(saetze, saat);
        status.textContent = "";
        zeigeNaechsten();
      } catch {
        status.textContent = "Ließ sich nicht zurücksetzen.";
        beschaeftigt = false;
        zeigeNaechsten();
      } finally {
        vonVorn.disabled = false;
      }
    })();
  });

  // Laden: was schon bewertet ist, wird übersprungen (gültig nur bei gleichem Wortlaut — prüft der Server).
  buehne.hidden = true;
  hoeren.disabled = true;
  vonVorn.disabled = true;
  sperreUrteile(true);
  status.textContent = "Lädt …";
  void (async () => {
    try {
      const r = await umgebung.holen(HOERPROBE_PFAD, { credentials: "same-origin" });
      const j = (await r.json().catch(() => ({}))) as { bewertungen?: (Bewertung & { pruefzahl?: number })[] };
      if (!r.ok) {
        status.textContent = r.status === 401 || r.status === 403 ? "Nur für Konto 1." : "Die Hörprobe lässt sich gerade nicht laden.";
        return;
      }
      beschaeftigt = false;
      vonVorn.disabled = false;
      for (const b of j.bewertungen ?? []) {
        if (saetze.some((s) => s.kennung === b.kennung && s.pruefzahl === b.pruefzahl)) bewertet.set(b.kennung, b.passt === true);
      }
      status.textContent = "";
      zeigeNaechsten();
    } catch {
      status.textContent = "Die Hörprobe lässt sich gerade nicht laden.";
    }
  })();

  return karte;
}
